import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Where a register field says it was read from, addressed to one open file.
 *
 * The extractor cites a chunk of its own splitting, and the viewer renders
 * those chunks as elements - so a citation is carried as the chunk itself
 * rather than as a character range to go hunting for in the rendered text.
 *
 * The rail shows two documents side by side and each has its own viewer, so a
 * citation is stored against the file it belongs to rather than as a single
 * "current" citation - clicking a field on the left must not move the document
 * on the right.
 */
export type CitationTarget = {
  /** `chunk_index` from the citation. The viewer wraps each chunk in one element. */
  chunkIndex: number;
  /**
   * Text that marks the cited line within the chunk, most specific first: a
   * list row's identity, a field's value. Entries are grounded to a line, and
   * a chunk can hold a whole schedule, so the viewer looks for the line and
   * lands on it - falling back to the top of the chunk where none of these is
   * on the page.
   */
  anchors?: string[];
  /** The register column, so the viewer can say what it scrolled to. */
  label: string;
  /**
   * Distinguishes one click from the next. Clicking the same field twice has
   * to scroll again, and identical payloads would otherwise be a no-op.
   */
  requestId: number;
};

export type CitationState = {
  /** Keyed by file id - the same id the register is given as `uploadId`. */
  targets: Record<number, CitationTarget>;
};

const initialState: CitationState = { targets: {} };

let nextRequestId = 0;

const citationSlice = createSlice({
  name: 'citation',
  initialState,
  reducers: {
    /** Scroll one file's viewer to the chunk, or the line in it, an entry cites. */
    showChunk: {
      reducer: (
        state,
        action: PayloadAction<{ fileId: number; target: CitationTarget }>
      ) => {
        state.targets[action.payload.fileId] = action.payload.target;
      },
      prepare: ({
        fileId,
        chunkIndex,
        label,
        anchors,
      }: {
        fileId: number;
        chunkIndex: number;
        label: string;
        anchors?: string[];
      }) => ({
        payload: {
          fileId,
          target: { chunkIndex, label, anchors, requestId: ++nextRequestId },
        },
      }),
    },
    clearCitation: (state, action: PayloadAction<number>) => {
      delete state.targets[action.payload];
    },
  },
});

export const { showChunk, clearCitation } = citationSlice.actions;
export default citationSlice.reducer;
